import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';

/**
 * Google Tag Manager 統合サービス
 * 
 * @description
 * 全プロジェクトで共通利用できる GTM サービス。
 * - ブラウザ環境でのみスクリプトを注入
 * - dataLayer へのイベント送信
 * - SSR対応
 * 
 * @example
 * ```typescript
 * export class App implements OnInit {
 *   private readonly gtmService = inject(GtmService);
 * 
 *   ngOnInit() {
 *     this.gtmService.init('GTM-K2WC2FV');
 * 
 *     // ページ遷移イベント送信
 *     this.router.events
 *       .pipe(filter((event) => event instanceof NavigationEnd))
 *       .subscribe((event) => {
 *         this.gtmService.pushEvent({
 *           event: 'page_view',
 *           url: event.urlAfterRedirects,
 *         });
 *       });
 *   }
 * }
 * ```
 * 
 * @author CAP
 */
@Injectable({
  providedIn: 'root',
})
export class GtmService {
  // ✅ 修正: コンストラクタインジェクションから inject() 関数に変更
  //private readonly platformId = inject(PLATFORM_ID);
constructor(@Inject(PLATFORM_ID) private readonly platformId: Object) {}
  /**
   * GTMスクリプトを初期化
   * 
   * @param gtmId GTMコンテナID（例: 'GTM-K2WC2FV'）
   * 
   * @description
   * - ブラウザ環境でのみ実行
   * - `<head>` タグに GTM スニペットを動的に追加
   * - SSR実行時は処理をスキップ
   */
  init(gtmId: string): void {
    // SSR実行時はスクリプトを注入しない(ブラウザのみ)
    if (!isPlatformBrowser(this.platformId)) return;

    // Google Tag Manager スニペット
    const script = document.createElement('script');
    script.innerHTML = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${gtmId}');
    `;
    document.head.appendChild(script);
  }

  /**
   * カスタムイベントをdataLayerに送信
   * 
   * @param event イベントオブジェクト
   * 
   * @description
   * - ブラウザ環境でのみ実行
   * - GTMの `dataLayer` にカスタムイベントを追加
   * 
   * @example
   * ```typescript
   * // ボタンクリックイベント
   * this.gtmService.pushEvent({
   *   event: 'button_click',
   *   buttonName: 'download_pdf',
   *   category: 'engagement'
   * });
   * 
   * // フォーム送信イベント
   * this.gtmService.pushEvent({
   *   event: 'form_submit',
   *   formType: 'contact',
   *   formData: { name: 'John', email: 'john@example.com' }
   * });
   * ```
   */
  pushEvent(event: object): void {
    if (isPlatformBrowser(this.platformId)) {
      (window as any).dataLayer = (window as any).dataLayer || [];
      (window as any).dataLayer.push(event);
    }
  }
}